import { useMemo, useState } from "react";
import {
  draftSpokenCountLines,
  type InventoryCountDictateDraftLine,
} from "@/lib/manufacturing/inventoryCountDictateApply";
import {
  matchSpokenLinesToCatalog,
  parseSpokenInventoryLines,
  type CatalogMatch,
} from "@/lib/manufacturing/spokenCatalogMatch";
import type { MfgInventoryCountLine } from "@/lib/manufacturing/types/inventoryCount";
import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { ManufacturingDictatePad } from "./ManufacturingDictatePad";
import { SpokenCatalogMatchList } from "./SpokenCatalogMatchList";

export function ManufacturingInventoryCountDictate(props: {
  lines: MfgInventoryCountLine[];
  sessionOpen: boolean;
  busy: boolean;
  onCommitQty: (lineId: string, countedQty: number | null) => void;
}) {
  const [matches, setMatches] = useState<CatalogMatch[]>([]);
  const [pending, setPending] = useState<InventoryCountDictateDraftLine[]>([]);
  const [parseHint, setParseHint] = useState<string | null>(null);
  const [padReset, setPadReset] = useState(0);

  const catalog = useMemo(
    () =>
      props.lines.map((l) => ({
        id: l.material_id,
        name: l.material_name ?? l.material_id,
        code: l.material_code,
      })),
    [props.lines],
  );

  const readyCount = pending.filter((p) => p.lineId && Number.isFinite(Number(p.qty))).length;

  function handleExtract(transcript: string) {
    const spoken = parseSpokenInventoryLines(transcript);
    if (spoken.length === 0) {
      setMatches([]);
      setPending([]);
      setParseHint(
        "Couldn't find a quantity and item name. Try “quantity 20 item 2x4x96 pressure treated”.",
      );
      return;
    }
    const next = matchSpokenLinesToCatalog(spoken, catalog);
    setMatches(next);
    setPending(draftSpokenCountLines(props.lines, next));
    setParseHint(
      next.every((m) => m.status === "unmatched")
        ? "Heard a line but nothing on this count sheet matched. Check the name or add the item first."
        : null,
    );
  }

  function applyAccepted() {
    let applied = 0;
    for (const row of pending) {
      if (!row.lineId) continue;
      const qty = Number(row.qty);
      if (!Number.isFinite(qty)) continue;
      props.onCommitQty(row.lineId, qty);
      applied += 1;
    }
    if (applied === 0) {
      setParseHint("Accept at least one matched line with a quantity.");
      return;
    }
    setMatches([]);
    setPending([]);
    setParseHint(null);
    setPadReset((n) => n + 1);
  }

  if (!props.sessionOpen) return null;

  return (
    <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 dark:border-violet-900 dark:bg-violet-950/20">
      <div className="text-xs font-semibold text-gray-800 dark:text-zinc-100">Dictate count</div>
      <p className="mb-2 text-[11px] text-gray-500">
        Say “quantity twenty item two by four by ninety six pressure treated.” Quantity then item
        keeps the count from blending into the size. Match builds a review list; Apply writes
        matching rows onto this count sheet locally.
      </p>
      <ManufacturingDictatePad
        disabled={props.busy}
        placeholder="quantity 20 item 2x4x96 pressure treated…"
        extractLabel="Match materials"
        resetKey={padReset}
        onComplete={handleExtract}
      />
      <SpokenCatalogMatchList matches={matches} emptyHint={parseHint} />
      {pending.length > 0 ? (
        <div className="mt-2 space-y-2">
          {pending.map((row) => (
            <div key={row.key} className="grid grid-cols-[1fr_5.5rem] items-center gap-2 text-[11px]">
              <div className="font-medium text-gray-800 dark:text-zinc-100">
                {row.materialName}
                {row.match === "unmatched" || !row.lineId
                  ? " — unmatched"
                  : ` · ${row.match}`}
              </div>
              <input
                className={inputClass}
                value={row.qty}
                onChange={(e) =>
                  setPending((prev) =>
                    prev.map((p) => (p.key === row.key ? { ...p, qty: e.target.value } : p)),
                  )
                }
              />
            </div>
          ))}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={props.busy}
              onClick={() => {
                setPending([]);
                setMatches([]);
                setParseHint(null);
                setPadReset((n) => n + 1);
              }}
            >
              Discard
            </button>
            <button
              type="button"
              className={mfgPrimaryBtnClass}
              disabled={props.busy || readyCount === 0}
              onClick={applyAccepted}
            >
              Apply {readyCount} to count sheet
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
